import {Inject, Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {ServerEndpoints} from '../../../server.endpoints';
import {
  DeviceCredentialRotation, DeviceCredentialSecret, DeviceCredentialSummary
} from '../shared/device-credential.models';

/**
 * Thin HTTP client for the monolith's register-credential API (`/device/credentials`), reached through the
 * gateway under the SPA's single `BASE_URL` origin so the auth interceptor attaches the context token.
 *
 * ### Two responses carry a plaintext password, and they are the only two
 *
 * {@link issue} and {@link rotate} answer a `DeviceCredentialSecret`. The server keeps a PBKDF2 verifier and
 * nothing reversible, so those responses are the only place the password will ever exist. They are handed
 * straight to the dialog that displays them and are never written anywhere else.
 *
 * ### A credential names no account (#279)
 *
 * It carries only the company + shard it was issued for; a register presenting it receives a token whose roles
 * are exactly `[Register]`. So {@link issue} takes a label and an optional shard and nothing else — the former
 * `userId` and the anchor-account picker it fed are retired.
 */
@Injectable({providedIn: 'root'})
export class DeviceCredentialHttpService {
  constructor(private http: HttpClient, @Inject('BASE_URL') private baseUrl: string) {
  }

  private get base(): string {
    return this.baseUrl + ServerEndpoints.Device_Credentials;
  }

  /**
   * The shard's credentials. Carries no password and no verifier — there is nothing reversible to return.
   *
   * Omitting `shardId` asks the server for the caller's own shard; a platform owner is then answered with the
   * company's whole estate, which is why the table shows a shard column as soon as the rows span more than one.
   */
  list(shardId?: string | null): Observable<DeviceCredentialSummary[]> {
    const params = shardId ? new HttpParams().set('shardId', shardId) : undefined;
    return this.http.get<DeviceCredentialSummary[]>(this.base, {params});
  }

  /**
   * Creates a credential. The password is in this response and nowhere else.
   *
   * @param name    the administrator's own label ("Tresen"). Never part of authentication.
   * @param shardId the shard the credential is for. Omitted, the server uses the caller's own shard.
   */
  issue(name: string, shardId?: string | null): Observable<DeviceCredentialSecret> {
    return this.http.post<DeviceCredentialSecret>(this.base, {name, shardId: shardId ?? null});
  }

  /**
   * Replaces a credential's password, keeping its client id — so the tills at that venue are given a new
   * password rather than re-identified. Answers the new plaintext, once.
   */
  rotate(id: string): Observable<DeviceCredentialSecret> {
    return this.http.post<DeviceCredentialSecret>(`${this.base}/${encodeURIComponent(id)}/rotate`, {});
  }

  /**
   * Permanently retires a credential. Register **sessions are untouched**: this stops the credential
   * acquiring *new* tokens; a till already holding one keeps working until its own token expires.
   */
  revoke(id: string): Observable<DeviceCredentialSummary> {
    return this.http.post<DeviceCredentialSummary>(`${this.base}/${encodeURIComponent(id)}/revoke`, {});
  }

  /** The company's rotation interval, already clamped by the server into the permitted range. */
  rotation(): Observable<DeviceCredentialRotation> {
    return this.http.get<DeviceCredentialRotation>(`${this.base}/rotation`);
  }

  /** Sets the company's rotation interval. Anything outside 1 … 365 days is refused by name, not clamped. */
  setRotation(days: number): Observable<DeviceCredentialRotation> {
    return this.http.put<DeviceCredentialRotation>(`${this.base}/rotation`, {days});
  }
}
